import { html } from "htm/preact";
import {
  TextFieldEntry,
  TextAreaEntry,
  CheckboxEntry,
  isTextFieldEntryEdited,
} from "@bpmn-io/properties-panel";
import { useService } from "bpmn-js-properties-panel";
import type { Element } from "diagram-js/lib/model/Types";

// ─────────────────────────────────────────────────────────────────────────────
// Design tokens — light neutral + blue/green
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  bgPanel: "#ffffff",
  bgAlt: "#f1f5f9",
  bgInput: "#ffffff",
  bgForm: "#f0f7ff",

  blue: "rgb(21, 101, 192)",
  blueLight: "rgba(21,101,192,0.08)",
  blueBorder: "rgba(21,101,192,0.30)",
  blueText: "rgb(21, 101, 192)",
  blueDark: "rgb(13,71,161)",

  green: "rgb(76, 175, 80)",
  greenMid: "rgba(76,175,80,0.15)",
  greenLight: "rgba(76,175,80,0.07)",
  greenBorder: "rgba(76,175,80,0.32)",
  greenText: "rgb(46,125,50)",

  text: "#1e293b",
  textSub: "#475569",
  textMuted: "#94a3b8",

  border: "rgba(148,163,184,0.25)",
  borderMid: "rgba(148,163,184,0.40)",

  r: "5px",
  rMd: "7px",
  rLg: "9px",
};

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

// What "params" can be:
//   "empty"   → not set yet
//   "scalar"  → "params": "some string"
//   "object"  → "params": { ... }  with a flat/nested tree
type GroupKind = "empty" | "scalar" | "object";

// An item inside an object group
interface TreeNode {
  key: string;
  value: string;
  isLong: boolean;
  children: TreeNode[] | null; // null = leaf, array = sub-object
  collapsed: boolean;
}

// All state for one group field — stored on businessObject, never re-created
interface GroupState {
  kind: GroupKind;
  // scalar
  scalarValue: string;
  scalarLong: boolean;
  // object
  nodes: TreeNode[];
  // add-form
  formOpen: boolean;
  formParentPath: number[] | null;
  sourceRaw: any;
}

interface CasePropsItem {
  element: Element;
  id: string;
  entry: any;
  description: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function capitalizeFirst(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function hasValue(val: any): boolean {
  if (val === null || val === undefined) return false;
  if (typeof val === "boolean") return true;
  if (typeof val === "string") {
    const t = val.trim();
    return t !== "" && t !== "{}" && t !== "[]";
  }
  if (typeof val === "object") {
    try {
      const s = JSON.stringify(val);
      return s !== "{}" && s !== "[]" && s !== "null";
    } catch {
      return false;
    }
  }
  return false;
}

function tryParse(val: any): any {
  if (!val) return null;
  if (typeof val !== "string") return val;
  try {
    return JSON.parse(val);
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Tree ↔ JSON
// ─────────────────────────────────────────────────────────────────────────────

function objToNodes(obj: Record<string, any>): TreeNode[] {
  return Object.entries(obj).map(([key, val]) => {
    if (val !== null && typeof val === "object" && !Array.isArray(val))
      return {
        key,
        value: "",
        isLong: false,
        children: objToNodes(val),
        collapsed: false,
      };
    const str = typeof val === "string" ? val : JSON.stringify(val);
    return {
      key,
      value: str,
      isLong: str.length > 120,
      children: null,
      collapsed: false,
    };
  });
}

function nodesToObj(nodes: TreeNode[]): Record<string, any> {
  const r: Record<string, any> = {};
  for (const n of nodes) {
    if (n.children !== null) {
      r[n.key] = nodesToObj(n.children);
      continue;
    }
    const p = tryParse(n.value);
    r[n.key] = p !== null ? p : n.value;
  }
  return r;
}

// ─────────────────────────────────────────────────────────────────────────────
// GroupState — single source of truth per field on businessObject
// ─────────────────────────────────────────────────────────────────────────────

const GS_KEY = "__gs__";

function makeEmpty(): GroupState {
  return {
    kind: "empty",
    scalarValue: "",
    scalarLong: false,
    nodes: [],
    formOpen: false,
    formParentPath: null,
    sourceRaw: undefined,
  };
}

function getGS(element: Element, field: string): GroupState {
  const bo = element.businessObject as any;
  const k = GS_KEY + field;
  const raw = bo[field];
  const cached = bo[k];

  // Le cache n'est valide que si la valeur brute n'a pas changé depuis
  if (cached && cached.sourceRaw === raw) return cached;

  const gs = makeEmpty();
  gs.sourceRaw = raw;
  if (raw !== undefined && raw !== null && raw !== "") {
    const parsed = tryParse(raw);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      !Array.isArray(parsed)
    ) {
      gs.kind = "object";
      gs.nodes = objToNodes(parsed);
    } else {
      gs.kind = "scalar";
      gs.scalarValue = typeof raw === "string" ? raw : JSON.stringify(raw);
      gs.scalarLong = gs.scalarValue.length > 120;
    }
  }
  bo[k] = gs;
  return gs;
}

// ─────────────────────────────────────────────────────────────────────────────
// Flush / commit
// ─────────────────────────────────────────────────────────────────────────────

const _timers: Record<string, ReturnType<typeof setTimeout>> = {};

function serialize(gs: GroupState): string {
  if (gs.kind === "scalar") return gs.scalarValue;
  if (gs.kind === "object") return JSON.stringify(nodesToObj(gs.nodes));
  return "";
}

function flush(element: Element, field: string, modeling: any) {
  const gs = getGS(element, field);
  const val = serialize(gs);
  (element.businessObject as any)[field] = val;
  gs.sourceRaw = val; // ← NOUVEAU : le cache reste cohérent avec ce qu'on vient d'écrire
  modeling.updateProperties(element, { [field]: val });
}

function scheduleFlush(element: Element, field: string, modeling: any) {
  const k = (element as any).id + field;
  clearTimeout(_timers[k]);
  _timers[k] = setTimeout(() => flush(element, field, modeling), 600);
}

// mutate gs in-place; immediate=true → flush + force re-render
function mutate(
  element: Element,
  field: string,
  fn: (gs: GroupState) => void,
  modeling: any,
  immediate: boolean,
) {
  fn(getGS(element, field));
  if (immediate) {
    flush(element, field, modeling);
    const bo = element.businessObject as any;
    const tick = (bo["__tick__"] || 0) + 1;
    bo["__tick__"] = tick;
    modeling.updateProperties(element, { __tick__: tick });
  } else {
    scheduleFlush(element, field, modeling);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Tree node helpers
// ─────────────────────────────────────────────────────────────────────────────

function getNodeAt(nodes: TreeNode[], path: number[]): TreeNode | null {
  let cur = nodes,
    node: TreeNode | null = null;
  for (const i of path) {
    node = cur[i] ?? null;
    if (!node) return null;
    cur = node.children ?? [];
  }
  return node;
}

function removeAt(nodes: TreeNode[], path: number[]): TreeNode[] {
  const [h, ...rest] = path;
  if (!rest.length) return nodes.filter((_, i) => i !== h);
  return nodes.map((n, i) =>
    i !== h ? n : { ...n, children: removeAt(n.children ?? [], rest) },
  );
}

function addAt(
  nodes: TreeNode[],
  parentPath: number[] | null,
  newNode: TreeNode,
): TreeNode[] {
  if (!parentPath || !parentPath.length) return [...nodes, newNode];
  const [h, ...rest] = parentPath;
  return nodes.map((n, i) => {
    if (i !== h) return n;
    if (!rest.length)
      return {
        ...n,
        children: [...(n.children ?? []), newNode],
        collapsed: false,
      };
    return { ...n, children: addAt(n.children ?? [], rest, newNode) };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────────────────

let _css = false;
function injectCSS() {
  if (_css || typeof document === "undefined") return;
  _css = true;
  const s = document.createElement("style");
  s.textContent = `
    .djg-input:focus{border-color:rgb(21,101,192)!important;box-shadow:0 0 0 2px rgba(21,101,192,.13)!important;outline:none!important}
    .djg-input::placeholder{color:#b0bec5}
    .djg-del:hover{color:#e53935!important;background:rgba(229,57,53,.07)!important}
    .djg-add-sub:hover{background:rgba(76,175,80,.15)!important}
    .djg-add-root:hover{background:rgba(21,101,192,.08)!important;border-color:rgb(21,101,192)!important}
    .djg-kind-btn{cursor:pointer;padding:4px 12px;font-size:12px;border-radius:4px;border:1px solid rgba(21,101,192,.3);background:transparent;color:rgb(21,101,192);transition:all .15s;font-family:inherit}
    .djg-kind-btn:hover{background:rgba(21,101,192,.08)}
    .djg-kind-btn-green{border-color:rgba(76,175,80,.35);color:rgb(46,125,50)}
    .djg-kind-btn-green:hover{background:rgba(76,175,80,.08)!important}
  `;
  document.head.appendChild(s);
}

const sInput: Record<string, any> = {
  width: "100%",
  fontSize: "12px",
  lineHeight: "1.5",
  padding: "4px 9px",
  background: T.bgInput,
  color: T.text,
  border: `1px solid ${T.borderMid}`,
  borderRadius: T.r,
  boxSizing: "border-box" as const,
  outline: "none",
  fontFamily: "inherit",
  transition: "border-color .15s,box-shadow .15s",
};
const sDelBtn = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: T.textMuted,
  fontSize: "15px",
  lineHeight: 1,
  padding: "0 3px",
  flexShrink: 0,
  borderRadius: "3px",
  transition: "color .15s,background .15s",
};

// ─────────────────────────────────────────────────────────────────────────────
// AddForm — reads from DOM on confirm, no state, no re-render during interaction
//
// For "field" / "textarea" added at the ROOT of an object group:
//   → NO key input (the value goes directly into the group as a root-level item
//     BUT since JSON objects need keys, we still need a key for object children)
//   Wait — re-reading the spec:
//
//   "champ simple" / "textarea" → params = "value"  (scalar, NO key anywhere)
//   "objet direct" → params = { key1: val1, key2: val2, ... }
//     → when ADDING items inside this object, each item DOES have a key
//   So:
//   - At the TOP LEVEL: scalar has no key. Object items ALWAYS have a key.
//   - The "no key" rule applies ONLY to the top-level scalar choice.
//   - Inside an object (root or nested), items always need a key.
// ─────────────────────────────────────────────────────────────────────────────

function AddForm({
  element,
  field,
  modeling,
  translate,
}: {
  element: Element;
  field: string;
  modeling: any;
  translate: any;
}) {
  injectCSS();
  const gs = getGS(element, field);
  const formId = `djg-form-${field}`;

  // ── helpers that manipulate DOM without triggering any re-render ──────────

  const getMode = (): "field" | "textarea" | "object" => {
    const root = document.getElementById(formId);
    const active = root?.querySelector<HTMLElement>(".djg-tab-on");
    return (active?.dataset.mode ?? "field") as "field" | "textarea" | "object";
  };

  const switchMode = (mode: "field" | "textarea" | "object") => {
    const root = document.getElementById(formId);
    if (!root) return;
    // update tab styles
    root.querySelectorAll<HTMLElement>(".djg-ftab").forEach((btn) => {
      const on = btn.dataset.mode === mode;
      btn.className = "djg-ftab " + (on ? "djg-tab-on" : "djg-tab-off");
    });
    // show/hide rows
    const valRow = root.querySelector<HTMLElement>(".djg-fval-row");
    const txtRow = root.querySelector<HTMLElement>(".djg-ftxt-row");
    const objHint = root.querySelector<HTMLElement>(".djg-fobj-hint");
    if (valRow) valRow.style.display = mode === "field" ? "block" : "none";
    if (txtRow) txtRow.style.display = mode === "textarea" ? "block" : "none";
    if (objHint) objHint.style.display = mode === "object" ? "block" : "none";
  };

  // ── confirm: read DOM, mutate state, debounced flush — NO forceRerender ───
  // We close the form by mutating gs.formOpen=false and calling a lightweight
  // tick that only updates __tick__ (causes panel re-render without canvas re-render)
  const confirm = () => {
    const root = document.getElementById(formId);
    if (!root) return;
    const keyEl = root.querySelector<HTMLInputElement>(".djg-fkey");
    const valEl = root.querySelector<HTMLInputElement | HTMLTextAreaElement>(
      ".djg-fval, .djg-ftxtval",
    );
    const key = (keyEl?.value ?? "").trim();
    const val = valEl?.value ?? "";
    const mode = getMode();

    if (!key) {
      keyEl?.focus();
      return;
    }

    const newNode: TreeNode =
      mode === "object"
        ? { key, value: "", isLong: false, children: [], collapsed: false }
        : {
            key,
            value: val,
            isLong: val.length > 120,
            children: null,
            collapsed: false,
          };

    // 1. mutate in memory
    const gsNow = getGS(element, field);
    gsNow.nodes = addAt(gsNow.nodes, gsNow.formParentPath, newNode);
    gsNow.formOpen = false;
    gsNow.formParentPath = null;

    // 2. flush to bpmn-js (debounced — no immediate re-render of canvas)
    scheduleFlush(element, field, modeling);

    // 3. re-render panel only (no canvas re-render = no focus loss)
    const bo = element.businessObject as any;
    const tick = (bo["__tick__"] || 0) + 1;
    bo["__tick__"] = tick;
    modeling.updateProperties(element, { __tick__: tick });
  };

  const cancel = () => {
    const gsNow = getGS(element, field);
    gsNow.formOpen = false;
    gsNow.formParentPath = null;
    const bo = element.businessObject as any;
    const tick = (bo["__tick__"] || 0) + 1;
    bo["__tick__"] = tick;
    modeling.updateProperties(element, { __tick__: tick });
  };

  const parentLabel = (() => {
    if (!gs.formParentPath || !gs.formParentPath.length) return "groupe";
    const n = getNodeAt(gs.nodes, gs.formParentPath);
    return n ? `"${n.key}"` : "groupe";
  })();

  return html`
    <div
      id=${formId}
      style=${{
        background: T.bgForm,
        border: `1px solid ${T.blueBorder}`,
        borderLeft: `3px solid ${T.blue}`,
        borderRadius: T.rMd,
        padding: "10px 12px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        marginTop: "6px",
      }}
    >
      <!-- header -->
      <div style=${{ display: "flex", alignItems: "center", gap: "6px" }}>
        <span
          style=${{
            fontSize: "11px",
            color: T.blue,
            fontWeight: "700",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
          >Nouvelle propriété</span
        >
        <span style=${{ fontSize: "11px", color: T.textMuted }}
          >→ ${parentLabel}</span
        >
      </div>

      <!-- type tabs (no select, pure DOM manipulation) -->
      <div style=${{ display: "flex", gap: "5px" }}>
        <button
          class="djg-ftab djg-tab-on"
          data-mode="field"
          onClick=${() => switchMode("field")}
          style=${{
            fontSize: "11px",
            padding: "3px 10px",
            borderRadius: "4px",
            cursor: "pointer",
            fontFamily: "inherit",
            border: "1px solid rgb(21,101,192)",
            background: "rgb(21,101,192)",
            color: "#fff",
          }}
        >
          Champ simple
        </button>
        <button
          class="djg-ftab djg-tab-off"
          data-mode="textarea"
          onClick=${() => switchMode("textarea")}
          style=${{
            fontSize: "11px",
            padding: "3px 10px",
            borderRadius: "4px",
            cursor: "pointer",
            fontFamily: "inherit",
            border: "1px solid rgba(21,101,192,0.3)",
            background: "transparent",
            color: "rgb(21,101,192)",
          }}
        >
          Texte long
        </button>
        <button
          class="djg-ftab djg-tab-off"
          data-mode="object"
          onClick=${() => switchMode("object")}
          style=${{
            fontSize: "11px",
            padding: "3px 10px",
            borderRadius: "4px",
            cursor: "pointer",
            fontFamily: "inherit",
            border: "1px solid rgba(76,175,80,0.4)",
            background: "transparent",
            color: "rgb(46,125,50)",
          }}
        >
          Objet JSON
        </button>
      </div>

      <!-- key input -->
      <input
        class="djg-input djg-fkey"
        type="text"
        placeholder="clé"
        style=${{ ...sInput, fontFamily: "monospace" }}
        onKeyDown=${(e: KeyboardEvent) => {
          if (e.key === "Enter") confirm();
        }}
      />

      <!-- field value -->
      <div class="djg-fval-row" style=${{ display: "block" }}>
        <input
          class="djg-input djg-fval"
          type="text"
          placeholder="valeur"
          style=${sInput}
          onKeyDown=${(e: KeyboardEvent) => {
            if (e.key === "Enter") confirm();
          }}
        />
      </div>

      <!-- textarea value -->
      <div class="djg-ftxt-row" style=${{ display: "none" }}>
        <textarea
          class="djg-input djg-ftxtval"
          rows="3"
          placeholder="valeur"
          style=${{ ...sInput, resize: "vertical" }}
        />
      </div>

      <!-- object hint -->
      <p
        class="djg-fobj-hint"
        style=${{
          margin: 0,
          fontSize: "11px",
          color: T.textMuted,
          fontStyle: "italic",
          display: "none",
        }}
      >
        Objet vide créé — ajoutez des sous-propriétés via "+ sous-propriété".
      </p>

      <!-- actions -->
      <div style=${{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
        <button
          onClick=${cancel}
          style=${{
            fontSize: "12px",
            padding: "4px 12px",
            background: "transparent",
            color: T.textSub,
            border: `1px solid ${T.borderMid}`,
            borderRadius: T.r,
            cursor: "pointer",
          }}
          onMouseEnter=${(e: Event) =>
            ((e.currentTarget as HTMLElement).style.background = T.bgAlt)}
          onMouseLeave=${(e: Event) =>
            ((e.currentTarget as HTMLElement).style.background = "transparent")}
        >
          Annuler
        </button>
        <button
          onClick=${confirm}
          style=${{
            fontSize: "12px",
            padding: "4px 16px",
            fontWeight: "600",
            background: T.blue,
            color: "#fff",
            border: "none",
            borderRadius: T.r,
            cursor: "pointer",
          }}
          onMouseEnter=${(e: Event) =>
            ((e.currentTarget as HTMLElement).style.background = T.blueDark)}
          onMouseLeave=${(e: Event) =>
            ((e.currentTarget as HTMLElement).style.background = T.blue)}
        >
          Ajouter
        </button>
      </div>
    </div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────────
// TreeNodeRow — one node in the object tree
// ─────────────────────────────────────────────────────────────────────────────

const INDENT = 14;

interface NodeRowProps {
  node: TreeNode;
  path: number[];
  element: Element;
  field: string;
  modeling: any;
  translate: any;
  onAddChild: (p: number[]) => void;
}

function TreeNodeRow({
  node,
  path,
  element,
  field,
  modeling,
  translate,
  onAddChild,
}: NodeRowProps) {
  injectCSS();
  const depth = path.length - 1;
  const isObj = node.children !== null;
  const pl = 10 + depth * INDENT;

  const headerBg = isObj
    ? depth === 0
      ? T.greenLight
      : "rgba(76,175,80,0.04)"
    : depth === 0
      ? T.blueLight
      : "transparent";
  const borderL =
    depth > 0
      ? `2px solid ${isObj ? T.greenBorder : T.blueBorder}`
      : `3px solid ${isObj ? T.green : T.blue}`;

  const del = (stopProp = false) => html`
    <button
      class="djg-del"
      onClick=${(e: Event) => {
        if (stopProp) e.stopPropagation();
        mutate(
          element,
          field,
          (gs) => {
            gs.nodes = removeAt(gs.nodes, path);
          },
          modeling,
          true,
        );
      }}
      style=${sDelBtn}
    >
      ×
    </button>
  `;

  // ── leaf (field / textarea) ───────────────────────────────────────────────
  if (!isObj) {
    return html`
      <div style=${{ borderBottom: `1px solid ${T.border}` }}>
        <div
          style=${{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: `4px 8px 4px ${pl}px`,
            background: headerBg,
            borderLeft: borderL,
          }}
        >
          <!-- key shown as label (read-only) -->
          <span
            style=${{
              fontSize: "11px",
              fontFamily: "monospace",
              fontWeight: depth === 0 ? "600" : "400",
              color: depth === 0 ? T.textSub : T.textMuted,
              whiteSpace: "nowrap",
              flexShrink: 0,
              minWidth: "50px",
            }}
            >${node.key}</span
          >
          ${node.isLong
            ? html`<textarea
                class="djg-input"
                rows="2"
                style=${{
                  ...sInput,
                  flex: 1,
                  resize: "vertical",
                  padding: "3px 8px",
                }}
                defaultValue=${node.value}
                onInput=${(e: Event) =>
                  mutate(
                    element,
                    field,
                    (gs) => {
                      const n = getNodeAt(gs.nodes, path);
                      if (n) n.value = (e.target as HTMLTextAreaElement).value;
                    },
                    modeling,
                    false,
                  )}
              />`
            : html`<input
                type="text"
                class="djg-input"
                style=${{ ...sInput, flex: 1, padding: "3px 8px" }}
                defaultValue=${node.value}
                onInput=${(e: Event) =>
                  mutate(
                    element,
                    field,
                    (gs) => {
                      const n = getNodeAt(gs.nodes, path);
                      if (n) n.value = (e.target as HTMLInputElement).value;
                    },
                    modeling,
                    false,
                  )}
              />`}
          ${del(false)}
        </div>
      </div>
    `;
  }

  // ── object node ───────────────────────────────────────────────────────────
  return html`
    <div style=${{ borderBottom: `1px solid ${T.border}` }}>
      <div
        style=${{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: `5px 8px 5px ${pl}px`,
          background: headerBg,
          borderLeft: borderL,
          cursor: "pointer",
          userSelect: "none" as const,
        }}
        onClick=${() =>
          mutate(
            element,
            field,
            (gs) => {
              const n = getNodeAt(gs.nodes, path);
              if (n) n.collapsed = !n.collapsed;
            },
            modeling,
            true,
          )}
      >
        <span
          style=${{
            width: "14px",
            textAlign: "center",
            flexShrink: 0,
            fontSize: "10px",
            color: T.textSub,
          }}
          >${node.collapsed ? "▶" : "▼"}</span
        >
        <span
          style=${{
            flex: 1,
            fontSize: "12px",
            fontFamily: "monospace",
            fontWeight: depth === 0 ? "600" : "500",
            color: depth === 0 ? T.text : T.textSub,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          >${node.key}</span
        >
        <span
          style=${{
            fontSize: "10px",
            fontWeight: "600",
            fontFamily: "monospace",
            color: T.greenText,
            background: T.greenMid,
            padding: "1px 6px",
            borderRadius: "4px",
            flexShrink: 0,
          }}
          >obj</span
        >
        ${del(true)}
      </div>
      ${!node.collapsed &&
      html`
        <div>
          ${(node.children ?? []).map(
            (child, i) => html`
              <${TreeNodeRow}
                key=${child.key + i}
                node=${child}
                path=${[...path, i]}
                element=${element}
                field=${field}
                modeling=${modeling}
                translate=${translate}
                onAddChild=${onAddChild}
              />
            `,
          )}
          <div
            style=${{
              padding: `5px 10px 6px ${pl + 22}px`,
              borderLeft: borderL,
            }}
          >
            <button
              class="djg-add-sub"
              onClick=${(e: Event) => {
                e.stopPropagation();
                onAddChild(path);
              }}
              style=${{
                fontSize: "11px",
                color: T.greenText,
                background: T.greenLight,
                border: `1px dashed ${T.greenBorder}`,
                borderRadius: T.r,
                padding: "2px 10px",
                cursor: "pointer",
                fontWeight: "500",
                transition: "background .15s",
              }}
            >
              + sous-propriété
            </button>
          </div>
        </div>
      `}
    </div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────────
// DynamicJsonGroup
// ─────────────────────────────────────────────────────────────────────────────

function DynamicJsonGroup({
  element,
  field,
}: {
  element: Element;
  field: string;
  groupLabel: string;
}) {
  injectCSS();
  const modeling = useService("modeling");
  const translate = useService("translate");
  const gs = getGS(element, field);

  // ── EMPTY: choose what params will be ────────────────────────────────────
  //
  //  [+ Champ simple]  → params = "value"
  //  [+ Texte long]    → params = "long value"
  //  [+ Objet JSON]    → params = { ... }   (direct items or named objects, same tree editor)
  //
  if (gs.kind === "empty") {
    return html`
      <div
        style=${{
          display: "flex",
          gap: "6px",
          marginBottom: "4px",
          flexWrap: "wrap",
        }}
      >
        <button
          class="djg-kind-btn"
          onClick=${() =>
            mutate(
              element,
              field,
              (gs) => {
                gs.kind = "scalar";
                gs.scalarLong = false;
              },
              modeling,
              true,
            )}
        >
          + Champ simple
        </button>
        <button
          class="djg-kind-btn"
          onClick=${() =>
            mutate(
              element,
              field,
              (gs) => {
                gs.kind = "scalar";
                gs.scalarLong = true;
              },
              modeling,
              true,
            )}
        >
          + Texte long
        </button>
        <button
          class="djg-kind-btn djg-kind-btn-green"
          onClick=${() =>
            mutate(
              element,
              field,
              (gs) => {
                gs.kind = "object";
                gs.nodes = [];
              },
              modeling,
              true,
            )}
        >
          + Objet JSON
        </button>
      </div>
    `;
  }

  // ── SCALAR: simple input or textarea, no key shown ────────────────────────
  //  params = "value"   — the value IS params, no key needed
  if (gs.kind === "scalar") {
    return html`
      <div style=${{ marginBottom: "4px" }}>
        <div
          style=${{
            border: `1px solid ${T.border}`,
            borderRadius: T.rLg,
            overflow: "hidden",
            background: T.bgPanel,
            borderLeft: `3px solid ${T.blue}`,
          }}
        >
          <div
            style=${{
              padding: "6px 8px",
              display: "flex",
              gap: "6px",
              alignItems: "flex-start",
            }}
          >
            ${gs.scalarLong
              ? html`<textarea
                  class="djg-input"
                  rows="4"
                  style=${{ ...sInput, flex: 1, resize: "vertical" }}
                  defaultValue=${gs.scalarValue}
                  onInput=${(e: Event) =>
                    mutate(
                      element,
                      field,
                      (gs) => {
                        gs.scalarValue = (
                          e.target as HTMLTextAreaElement
                        ).value;
                      },
                      modeling,
                      false,
                    )}
                />`
              : html`<input
                  type="text"
                  class="djg-input"
                  style=${{ ...sInput, flex: 1 }}
                  defaultValue=${gs.scalarValue}
                  onInput=${(e: Event) =>
                    mutate(
                      element,
                      field,
                      (gs) => {
                        gs.scalarValue = (e.target as HTMLInputElement).value;
                      },
                      modeling,
                      false,
                    )}
                />`}
            <button
              class="djg-del"
              onClick=${() =>
                mutate(
                  element,
                  field,
                  (gs) => {
                    gs.kind = "empty";
                    gs.scalarValue = "";
                  },
                  modeling,
                  true,
                )}
              style=${{ ...sDelBtn, marginTop: "3px" }}
            >
              ×
            </button>
          </div>
        </div>
        <button
          onClick=${() =>
            mutate(
              element,
              field,
              (gs) => {
                gs.kind = "object";
                gs.nodes = gs.scalarValue
                  ? [
                      {
                        key: "value",
                        value: gs.scalarValue,
                        isLong: gs.scalarLong,
                        children: null,
                        collapsed: false,
                      },
                    ]
                  : [];
                gs.scalarValue = "";
              },
              modeling,
              true,
            )}
          style=${{
            marginTop: "5px",
            fontSize: "11px",
            color: T.textMuted,
            background: "transparent",
            border: `1px dashed ${T.borderMid}`,
            borderRadius: T.r,
            padding: "2px 10px",
            cursor: "pointer",
          }}
        >
          Convertir en objet →
        </button>
      </div>
    `;
  }

  // ── OBJECT: tree editor ───────────────────────────────────────────────────
  //  params = { ... }
  //  Items can be:
  //    - leaf (field/textarea):   params = { "senderId": "SMTP_KEPLER", ... }
  //    - named object:            params = { "submit": { "label": "OK" }, ... }
  //  Both use the same tree. The form always asks for a key + value/type.

  const openAddForm = (parentPath: number[] | null) => {
    mutate(
      element,
      field,
      (gs) => {
        gs.formOpen = true;
        gs.formParentPath = parentPath;
      },
      modeling,
      true,
    );
  };

  return html`
    <div style=${{ marginBottom: "4px" }}>
      <div
        style=${{
          border: `1px solid ${T.border}`,
          borderRadius: T.rLg,
          overflow: "hidden",
          background: T.bgPanel,
        }}
      >
        ${gs.nodes.length === 0 &&
        !gs.formOpen &&
        html`
          <div
            style=${{
              padding: "10px",
              fontSize: "12px",
              color: T.textMuted,
              textAlign: "center",
              fontStyle: "italic",
            }}
          >
            Aucune propriété
          </div>
        `}
        ${gs.nodes.map(
          (node, i) => html`
            <${TreeNodeRow}
              key=${node.key + i}
              node=${node}
              path=${[i]}
              element=${element}
              field=${field}
              modeling=${modeling}
              translate=${translate}
              onAddChild=${openAddForm}
            />
          `,
        )}
      </div>

      ${gs.formOpen &&
      html`
        <${AddForm}
          element=${element}
          field=${field}
          modeling=${modeling}
          translate=${translate}
        />
      `}
      ${!gs.formOpen &&
      html`
        <div style=${{ display: "flex", gap: "6px", marginTop: "6px" }}>
          <button
            class="djg-add-root"
            onClick=${() => openAddForm(null)}
            style=${{
              flex: 1,
              fontSize: "12px",
              padding: "5px 10px",
              background: "transparent",
              color: T.blueText,
              border: `1px dashed ${T.blueBorder}`,
              borderRadius: T.r,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "5px",
              transition: "background .15s,border-color .15s",
            }}
          >
            <span
              style=${{ fontSize: "14px", lineHeight: 1, fontWeight: "300" }}
              >+</span
            >
            Ajouter une propriété
          </button>
          <button
            onClick=${() =>
              mutate(
                element,
                field,
                (gs) => {
                  gs.kind = "empty";
                  gs.nodes = [];
                  gs.formOpen = false;
                },
                modeling,
                true,
              )}
            style=${{
              fontSize: "12px",
              padding: "5px 10px",
              background: "transparent",
              color: T.textMuted,
              border: `1px dashed ${T.borderMid}`,
              borderRadius: T.r,
              cursor: "pointer",
            }}
          >
            ← Vider
          </button>
        </div>
      `}
    </div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export function buildDynamicJsonGroup(
  element: Element,
  field: string,
  groupLabel: string,
): any[] {
  return [
    {
      id: `dynamic-${field}`,
      element,
      component: () =>
        html`<${DynamicJsonGroup}
          element=${element}
          field=${field}
          groupLabel=${groupLabel}
        />`,
    },
  ];
}

function Item(props: CasePropsItem) {
  const { element, id, entry: EntryComponent } = props;
  const modeling = useService("modeling");
  const translate = useService("translate");
  const debounce = useService("debounceInput");
  const raw = (element.businessObject as any)[id];
  if (!hasValue(raw)) return null;
  const isCheckbox = EntryComponent === CheckboxEntry;
  const toBool = (v: any): boolean => {
    if (typeof v === "boolean") return v;
    if (typeof v === "string") return v === "true";
    return !!v;
  };
  const getValue = () => {
    const v = (element.businessObject as any)[id];
    if (isCheckbox) return toBool(v);
    if (v === undefined || v === null) return "";
    if (typeof v === "object") {
      try {
        return JSON.stringify(v, null, 2);
      } catch {
        return "";
      }
    }
    return String(v);
  };
  const setValue = (value: any) =>
    modeling.updateProperties(element, {
      [id]: isCheckbox ? toBool(value) : value,
    });
  return html`<${EntryComponent}
    id=${id}
    element=${element}
    description=""
    label=${translate(capitalizeFirst(id))}
    getValue=${getValue}
    setValue=${setValue}
    debounce=${debounce}
    tooltip=${translate(props.description || "")}
  />`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Props builders
// ─────────────────────────────────────────────────────────────────────────────

export default function caseProps(element: Element) {
  return [
    {
      id: "code",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "service",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "type",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "start",
      element,
      component: Item,
      entry: CheckboxEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "form",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "icon",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "codeTemplate",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "caseStatus",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "resume",
      element,
      component: Item,
      entry: CheckboxEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "task",
      element,
      component: Item,
      entry: CheckboxEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
  ];
}

export function endEventProps(element: Element) {
  return [
    {
      id: "code",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "label",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "service",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "type",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
  ];
}

export function processActionsProps(element: Element) {
  return [
    {
      id: "actions",
      element,
      component: Item,
      entry: TextAreaEntry,
      isEdited: isTextFieldEntryEdited,
      description: "Enter JSON data",
    },
  ];
}

export function processActivityActionsProps(element: Element) {
  return [
    {
      id: "activityActions",
      element,
      component: Item,
      entry: TextAreaEntry,
      isEdited: isTextFieldEntryEdited,
      description: "Enter JSON data",
    },
  ];
}

export function processFormsProps(element: Element) {
  return [
    {
      id: "forms",
      element,
      component: Item,
      entry: TextAreaEntry,
      isEdited: isTextFieldEntryEdited,
      description: "Enter JSON data",
    },
  ];
}

export function processFormProps(element: Element) {
  return [
    {
      id: "form",
      element,
      component: Item,
      entry: TextAreaEntry,
      isEdited: isTextFieldEntryEdited,
      description: "Enter JSON data",
    },
  ];
}

export function processViewProps(element: Element) {
  return [
    {
      id: "view",
      element,
      component: Item,
      entry: TextAreaEntry,
      isEdited: isTextFieldEntryEdited,
      description: "Enter JSON data",
    },
  ];
}

export function processFilterCategoryProps(element: Element) {
  return [
    {
      id: "code",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "condition",
      element,
      component: Item,
      entry: TextAreaEntry,
      isEdited: isTextFieldEntryEdited,
      description: "Enter JSON data",
    },
    {
      id: "label",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "rank",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
    {
      id: "icon",
      element,
      component: Item,
      entry: TextFieldEntry,
      isEdited: isTextFieldEntryEdited,
      description: "",
    },
  ];
}

export function processFiltersProps(element: Element) {
  return [
    {
      id: "filters",
      element,
      component: Item,
      entry: TextAreaEntry,
      isEdited: isTextFieldEntryEdited,
      description: "Enter JSON data",
    },
  ];
}
